import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaperPlaneRight, Robot, Plus, Trash, User, Lightning, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AIChat() {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState({ provider: "anthropic", model: "claude-sonnet-4-6" });
  const [quota, setQuota] = useState(null);
  const [autoEntry, setAutoEntry] = useState(false);
  const [autoPreview, setAutoPreview] = useState(null);
  const [lastImage, setLastImage] = useState(null);  // base64 of last scanned receipt
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const reloadQuota = () => api.get("/ai/quota").then((r) => setQuota(r.data));

  useEffect(() => {
    api.get("/ai/sessions").then((r) => setSessions(r.data));
    api.get("/ai/models").then((r) => { setModels(r.data.models); if (r.data.current) setCurrentModel(r.data.current); });
    reloadQuota();
  }, []);

  useEffect(() => {
    if (current) api.get(`/ai/sessions/${current}/messages`).then((r) => setMessages(r.data));
    else setMessages([]);
  }, [current]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;

    // Auto-Entry mode: parse via /ai/auto-entry (preview) or execute
    if (autoEntry) {
      const msg = input; setInput(""); setLoading(true);
      setMessages((m) => [...m, { role: "user", content: msg, id: `tmp-${Date.now()}` }]);
      try {
        const r = await api.post("/ai/auto-entry", { message: msg, execute: false });
        setAutoPreview(r.data);
        setMessages((m) => [...m, { role: "assistant", content: `⚡ Auto-Entry: ${r.data.confirmation_message || r.data.action || "Tidak jelas"}${r.data.action === "unknown" ? "" : "\n\nKlik ✓ Konfirmasi untuk eksekusi."}`, id: `auto-${Date.now()}` }]);
      } catch (e) { toast.error(e?.response?.data?.detail || "Gagal parse"); }
      finally { setLoading(false); reloadQuota(); }
      return;
    }

    const userMsg = { role: "user", content: input, id: `tmp-${Date.now()}` };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "", id: "streaming", streaming: true }]);
    const msg = input;
    setInput("");
    setLoading(true);

    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, session_id: current, provider: currentModel.provider, model: currentModel.model }),
      });
      if (resp.status === 429) {
        const err = await resp.json();
        toast.error(err.detail || "Kuota habis. Upgrade paket.");
        setMessages((m) => m.filter(x => x.id !== "streaming"));
        setLoading(false);
        reloadQuota();
        return;
      }
      if (!resp.ok || !resp.body) {
        throw new Error("Stream error");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let sessId = current;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n\n").filter(l => l.startsWith("data: "));
        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "delta") {
              acc += data.content;
              if (!sessId && data.session_id) sessId = data.session_id;
              setMessages((m) => m.map(x => x.id === "streaming" ? { ...x, content: acc } : x));
            } else if (data.type === "done") {
              sessId = data.session_id;
            } else if (data.type === "error") {
              toast.error(data.error);
            }
          } catch (_) { /* skip */ }
        }
      }
      if (!current && sessId) {
        setCurrent(sessId);
        api.get("/ai/sessions").then((r) => setSessions(r.data));
      }
      setMessages((m) => m.map(x => x.id === "streaming" ? { ...x, streaming: false, id: `assist-${Date.now()}` } : x));
      reloadQuota();
      window.dispatchEvent(new Event("nusa:quota-updated"));
    } catch (err) {
      toast.error("Gagal mengirim pesan ke AI");
      setMessages((m) => m.filter(x => x.id !== "streaming"));
    } finally {
      setLoading(false);
    }
  };

  const executeAutoEntry = async () => {
    if (!autoPreview) return;
    setLoading(true);
    try {
      const lastUser = [...messages].reverse().find(m => m.role === "user");
      const r = await api.post("/ai/auto-entry", {
        message: lastUser?.content || "",
        execute: true,
        image_base64: lastImage || undefined,
      });
      if (r.data.executed) {
        toast.success(`✓ Tersimpan: ${JSON.stringify(r.data.created)}`);
        setMessages((m) => [...m, { role: "assistant", content: `✓ Transaksi tersimpan: ${JSON.stringify(r.data.created, null, 2)}`, id: `done-${Date.now()}` }]);
      } else {
        toast.error(r.data.error || "Gagal eksekusi");
      }
      setAutoPreview(null);
      setLastImage(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setLoading(false); reloadQuota(); }
  };

  const scanReceipt = async (file) => {
    if (!file) return;
    const okType = ["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type);
    if (!okType) { toast.error("Format tidak didukung. Gunakan JPG/PNG/WEBP."); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error("Ukuran maks 6MB"); return; }
    setLoading(true);
    // Baca file -> base64
    const b64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(",", 2)[1]);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const previewUrl = URL.createObjectURL(file);
    setMessages((m) => [...m, {
      role: "user", id: `scan-${Date.now()}`,
      content: `📷 Scan bon: ${file.name}`,
      image_url: previewUrl,
    }]);
    try {
      const r = await api.post("/ai/auto-entry", {
        message: "Catat bon/struk ini sebagai beban.",
        execute: false,
        image_base64: b64,
      });
      setLastImage(b64);
      setAutoPreview(r.data);
      const data = r.data.data || {};
      const detail = r.data.action === "expense"
        ? `Vendor: ${data.vendor || "-"}\nTanggal: ${data.date || "-"}\nTotal: Rp ${(data.amount || 0).toLocaleString("id-ID")}\nKategori: ${data.category || "-"} (${data.expense_account_code || "6000"})`
        : (r.data.confirmation_message || "Tidak jelas");
      setMessages((m) => [...m, {
        role: "assistant", id: `scan-r-${Date.now()}`,
        content: `📷 Hasil pindai bon:\n\n${detail}\n\nKlik ✓ Konfirmasi untuk pos jurnal.`,
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal scan bon");
    } finally { setLoading(false); reloadQuota(); }
  };

  const newChat = async () => {
    setCurrent(null);
    setMessages([]);
  };

  const deleteSession = async (id) => {
    await api.delete(`/ai/sessions/${id}`);
    if (current === id) { setCurrent(null); setMessages([]); }
    api.get("/ai/sessions").then((r) => setSessions(r.data));
  };

  const setModel = async (id) => {
    const m = models.find(x => x.id === id);
    if (!m) return;
    setCurrentModel({ provider: m.provider, model: m.model });
    await api.post("/ai/model", { provider: m.provider, model: m.model });
    toast.success(`Model diubah ke ${m.label}`);
  };

  const modelId = `${currentModel.provider}/${currentModel.model}`;

  return (
    <div className="flex h-[calc(100vh-56px)]" data-testid="ai-chat-page">
      <div className="hidden sm:flex w-64 border-r border-slate-200 bg-white flex-col">
        <div className="p-3 border-b border-slate-200 space-y-2">
          <Button onClick={newChat} className="w-full bg-slate-900 hover:bg-slate-800 text-white h-9" data-testid="new-chat-btn"><Plus size={14} className="mr-1" /> Chat Baru</Button>
          <Select value={modelId} onValueChange={setModel}>
            <SelectTrigger className="h-9 text-xs" data-testid="model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {models.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {quota && (
            <div className="text-[11px] text-slate-500 text-center" data-testid="ai-quota">
              {quota.unlimited ? "Unlimited" : `${quota.used} / ${quota.limit} pesan bulan ini`}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-auto py-2">
          {sessions.length === 0 ? <div className="text-xs text-slate-500 text-center py-4">Belum ada percakapan</div> : sessions.map(s => (
            <div key={s.id} className={`group flex items-center justify-between px-3 py-2 text-sm cursor-pointer ${current === s.id ? "bg-slate-100" : "hover:bg-slate-50"}`} onClick={() => setCurrent(s.id)} data-testid={`session-${s.id}`}>
              <div className="truncate flex-1">{s.title}</div>
              <button className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600" onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}><Trash size={12} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        {messages.length === 0 && !current ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-md text-center" data-testid="ai-welcome">
              <div className="w-16 h-16 rounded-full bg-slate-900 text-amber-500 mx-auto flex items-center justify-center mb-4">
                <Robot size={32} weight="duotone" />
              </div>
              <h2 className="font-serif-display text-3xl font-medium text-slate-900">AI Bookkeeping</h2>
              <p className="text-slate-600 mt-2 text-sm">Tanya apa saja tentang keuangan bisnis Anda, atau minta bantuan mencatat transaksi.</p>
              <div className="mt-6 grid grid-cols-1 gap-2 text-left text-sm">
                {["Berapa laba bersih bulan ini?", "Catat beban sewa Rp 5 juta bulan ini", "Kategori apa untuk pembelian bahan baku?", "Bagaimana cash flow bisnis saya?"].map(q => (
                  <button key={q} onClick={() => setInput(q)} className="text-left px-4 py-2.5 bg-white border border-slate-200 rounded-md hover:border-amber-400 hover:bg-amber-50 transition-colors" data-testid={`suggestion-${q.slice(0,10)}`}>{q}</button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto p-6 space-y-4">
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-slate-900 text-amber-500 flex items-center justify-center shrink-0">
                    <Robot size={16} weight="duotone" />
                  </div>
                )}
                <div className={`max-w-2xl rounded-lg px-4 py-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  {m.image_url && <img src={m.image_url} alt="bon" className="max-w-[240px] rounded-md border border-slate-700 mb-2" data-testid={`msg-img-${i}`} />}
                  {m.content || (m.streaming ? "..." : "")}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center shrink-0">
                    <User size={16} weight="regular" />
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}

        <div className="border-t border-slate-200 p-4 bg-white">
          <div className="max-w-3xl mx-auto space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="auto-entry-toggle">
                <input type="checkbox" checked={autoEntry} onChange={(e) => { setAutoEntry(e.target.checked); setAutoPreview(null); }} />
                <Lightning size={14} weight={autoEntry ? "fill" : "regular"} className={autoEntry ? "text-amber-500" : "text-slate-400"} />
                <span className={autoEntry ? "text-amber-700 font-medium" : "text-slate-600"}>Mode Auto-Entry (AI langsung post transaksi)</span>
              </label>
              {autoPreview && autoPreview.action !== "unknown" && (
                <Button size="sm" onClick={executeAutoEntry} disabled={loading} className="ml-auto bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid="auto-entry-confirm">✓ Konfirmasi & Eksekusi</Button>
              )}
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) scanReceipt(f); e.target.value = ""; }}
                data-testid="scan-receipt-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                title="Scan bon / struk dengan AI"
                className="h-11 px-3 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="scan-receipt-btn"
              >
                <Camera size={18} weight="duotone" />
              </Button>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={autoEntry ? "Contoh: Jual 5 pcs ke PT ABC seharga Rp 500.000 hari ini" : "Tanya AI, atau tekan 📷 untuk scan bon..."}
                className="h-11"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button onClick={send} disabled={loading || !input.trim()} className="h-11 px-5 bg-amber-600 hover:bg-amber-700" data-testid="chat-send-btn">
                <PaperPlaneRight size={16} weight="fill" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
